import type { JSX } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import {
  Button,
  EmptyState,
  CriticalIcon,
  PageHeader,
  PageLayout,
  pageHeaderStyles,
  PlusIcon,
  Text,
  useLocalStorage,
} from '@buffer-mono/popcorn'

import { ALL_CHANNELS_TIMEZONE_STORAGE_KEY } from '~publish/components/AllChannelsTimeZoneSelect'
import { splitNoChannelsSelection } from '~publish/components/FilterByChannel'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { RegisteredBannersProvider } from '~publish/components/RegisteredBanner'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { TimezoneProvider } from '~publish/hooks/useTimezone'
import {
  PublishPageTitle,
  GetAllChannelsInfo,
} from '~publish/pages/AllChannels/AllChannelsPage'
import { ChannelAccessDenied } from '~publish/pages/Channel/ChannelAccessDenied'
import { ChannelPageTitle } from '~publish/pages/Channel/ChannelPageTitle'
import { ChannelProvider } from '~publish/pages/Channel/ChannelContext'
import { GetChannelInfo } from '~publish/pages/Channel/ChannelPage'
import { getDisabledChannelIds } from '~publish/utils/permissions/getDisabledChannelIds'
import { canPerform } from '~publish/utils/permissions/canPerform'
import {
  areAllChannelsViewOnlyForPublish,
  isViewOnlyPublishChannel,
} from '~publish/utils/permissions/viewOnlyPublish'
import { PostListOrCalendarViewToggle } from '~publish/components/PostListOrCalendarViewToggle/PostListOrCalendarViewToggle'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'

import { BoardColumns } from './BoardColumns'
import { BoardFilterBar } from './BoardFilterBar'
import { useBoardState } from './useBoardState'

import styles from './BoardPage.module.css'

function BoardPageError(): JSX.Element {
  return (
    <EmptyState size="xlarge" variant="critical">
      <EmptyState.Icon>
        <CriticalIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Failed to load</EmptyState.Heading>
      <EmptyState.Description>
        <Text color="critical">Something went wrong loading the board.</Text>
      </EmptyState.Description>
    </EmptyState>
  )
}

/**
 * Board's own page, mirroring `CalendarPage`: one component shared by the
 * all-channels route (`/schedule/board`) and the single-channel route
 * (`/channels/:id/schedule/board`), branching on an optional `:id` param.
 */
export const BoardPage = (): JSX.Element => {
  const channelId = useParams<{ id?: string }>().id
  const organizationId = useOrganizationId()

  const { data, error, loading } = useQuery(GetAllChannelsInfo, {
    skip: !organizationId,
  })
  const channels = sanitizeNullableArray(
    data?.account?.currentOrganization?.channels,
  )

  const { data: channelData, error: channelError } = useQuery(GetChannelInfo, {
    variables: { channelId: channelId ?? '' },
    skip: !channelId || !organizationId,
  })
  const channel = channelData?.channel

  const [allChannelsTimeZone] = useLocalStorage(
    ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
    CURRENT_TIME_ZONE,
  )

  const boardState = useBoardState({ prefillChannelId: channelId })
  const disabledChannelIds = getDisabledChannelIds(channels)
  const state = {
    ...boardState,
    channelIds: boardState.channelIds.filter(
      (id) => !disabledChannelIds.has(id),
    ),
  }
  const selectionHasNoAccessibleChannels =
    boardState.channelIds.length > 0 && state.channelIds.length === 0
  // Strip the "No channels" sentinel — it's not a real channel id to
  // preselect in the composer.
  const { realChannelIds } = splitNoChannelsSelection(state.channelIds)
  const isViewOnly = channel
    ? isViewOnlyPublishChannel(channel)
    : areAllChannelsViewOnlyForPublish(channels)

  if (error || (channelId && channelError)) {
    return <BoardPageError />
  }

  if (channelId && channel && !canPerform(channel, 'viewPublish')) {
    return <ChannelAccessDenied channel={channel} />
  }

  if (channelId && !channel) {
    // Still loading the single channel; the org-wide channels query below
    // already covers the loading skeleton for that half.
    return <></>
  }

  const title = channel ? (
    <ChannelProvider channel={channel}>
      <ChannelPageTitle />
    </ChannelProvider>
  ) : (
    <PublishPageTitle />
  )

  return (
    <TimezoneProvider timeZone={allChannelsTimeZone}>
      <RegisteredBannersProvider key="registered-banner-provider-board">
        <PageLayout className={styles.page}>
          <PageHeader>
            <PageHeader.Row>
              {title}
              <PageHeader.Actions>
                <div className={pageHeaderStyles.hideOnNarrow}>
                  <PostListOrCalendarViewToggle />
                </div>
                {!isViewOnly && (
                  <NewPostComposerTrigger
                    cta="publish-board-header-newPost-1"
                    channels={
                      realChannelIds.length ? realChannelIds : undefined
                    }
                  >
                    <Button
                      size="medium"
                      variant="secondary"
                      disabled={selectionHasNoAccessibleChannels}
                    >
                      <PlusIcon /> New Post
                    </Button>
                  </NewPostComposerTrigger>
                )}
              </PageHeader.Actions>
            </PageHeader.Row>
            <PageHeader.Row>
              <BoardFilterBar
                state={selectionHasNoAccessibleChannels ? boardState : state}
                channels={channels}
              />
            </PageHeader.Row>
          </PageHeader>
          <PageLayout.Container size="wide" className={styles.content}>
            {loading && channels.length === 0 ? (
              <></>
            ) : selectionHasNoAccessibleChannels ? (
              <EmptyState size="large">
                <EmptyState.Heading>Channels unavailable</EmptyState.Heading>
                <EmptyState.Description>
                  The selected channels aren’t available in Publish. Choose
                  another channel or clear the channel filter.
                </EmptyState.Description>
              </EmptyState>
            ) : (
              <BoardColumns
                state={state}
                channels={channels.filter(
                  (candidate) => !disabledChannelIds.has(candidate.id),
                )}
              />
            )}
          </PageLayout.Container>
        </PageLayout>
      </RegisteredBannersProvider>
      {/* Outlet for the nested posts/:postId dialog route */}
      <Outlet />
    </TimezoneProvider>
  )
}
